// dependencies
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import type {
	CreateReviewCycleRequestInput,
	CreateReviewCycleResponseJson,
	GetReviewCycleDetailsResponseFrontend,
	GetReviewCyclePreviewSubjectsResultsRequestInput,
	GetReviewCyclePreviewSubjectsResultsResponseJson,
	PostReviewCyclePreviewSubjectsRequestInput,
	PostReviewCyclePreviewSubjectsResponseJson,
	ReviewCycleDetailsLegacyResponseFrontend,
	ReviewDetailsFrontend,
} from "@performyard/service-contracts";

import {
	reviewCycleDetailsQuery,
	reviewCycleMetaQuery,
	reviewCycleReviewsDetailsStreamQuery,
	reviewCycleSubjectsQuery,
	reviewCyclesListQuery,
} from "../../queries";
import { queryClient } from "../../queryClient";
// api
import {
	type GetReviewCyclesParams,
	bulkApproveEasyAddFormsForReviewCycle,
	bulkDeactivateEasyAddFormsForReviewCycle,
	closeReviewCycle,
	createReviewCycle,
	deleteReviewCycle,
	deleteReviewFromReviewCycle,
	downloadGoalDiscussionCsv,
	getReviewCycleDetailsLegacy,
	getReviewCyclePreviewSubjectsResults,
	postReviewCyclePreviewSubjects,
	reviewCycleForms,
} from "../../services";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useReviewCycles = (params: GetReviewCyclesParams) => {
	return useQuery(reviewCyclesListQuery(params));
};

export const useReviewCycleForms = (reviewCycleID: string) => {
	return useQuery({
		queryKey: ["review-cycle-forms", reviewCycleID],
		queryFn: () => reviewCycleForms(reviewCycleID),
		meta: {
			errorMessage: "There was an error getting the review cycle forms.",
		},
		enabled: !!reviewCycleID,
	});
};

export const useReviewCycleDetailsLegacy = (
	reviewCycleID: string,
	options?: PYUseQueryOptions<ReviewCycleDetailsLegacyResponseFrontend>
) => {
	return useQuery<ReviewCycleDetailsLegacyResponseFrontend>({
		queryKey: ["review-cycle-details-legacy", reviewCycleID],
		queryFn: async () => await getReviewCycleDetailsLegacy(reviewCycleID),
		meta: {
			errorMessage: "There was an error getting the review cycle details.",
		},
		enabled: !!reviewCycleID,
		...options,
	});
};

export const useReviewCycleMeta = (reviewCycleID?: string) => {
	return useQuery(reviewCycleMetaQuery(reviewCycleID));
};

export const useReviewCycleSubjects = (reviewCycleId: string) => {
	return useQuery(reviewCycleSubjectsQuery(reviewCycleId));
};

export const useReviewCycleReviewsDetailsStream = (
	reviewCycleId: string,
	onChunk?: (chunk: { data: ReviewDetailsFrontend[] }) => void,
	options?: PYUseQueryOptions<ReviewDetailsFrontend[]>
) => {
	const resetQuery = () => {
		// Remove the cached data entirely to force fresh fetch (needed because of infinite staleTime)
		queryClient.removeQueries({ queryKey: reviewCycleReviewsDetailsStreamQuery(reviewCycleId).queryKey });
	};
	const query = useQuery<ReviewDetailsFrontend[]>({
		...reviewCycleReviewsDetailsStreamQuery(reviewCycleId, onChunk),
		...options,
	});

	return {
		...query,
		resetQuery,
	};
};

export const useDeleteReviewFromReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId, reviewId }: { reviewCycleId: string; reviewId: string }) =>
			deleteReviewFromReviewCycle(reviewCycleId, reviewId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			// Manually update the query data without the removed review
			queryClient.setQueryData(
				reviewCycleReviewsDetailsStreamQuery(variables.reviewCycleId).queryKey,
				(old: ReviewDetailsFrontend[]) => old.filter((review) => review._id !== variables.reviewId)
			);
			queryClient.invalidateQueries({ queryKey: reviewCycleDetailsQuery(variables.reviewCycleId).queryKey });
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useReviewCycleDetails = (
	reviewCycleId: string,
	options?: PYUseQueryOptions<GetReviewCycleDetailsResponseFrontend>
) => {
	return useQuery(reviewCycleDetailsQuery(reviewCycleId, options));
};

export const useCloseReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) => closeReviewCycle(reviewCycleId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			// Optimistically expect a job to appear on the WS shortly
			try {
				const { useReviewCycleStatusStore } = require("../../websocket/review-cycle-status");
				useReviewCycleStatusStore.getState().expectJobFor(2000);
			} catch (error) {
				console.error("Error optimistically expecting a job on the WS:", error);
			}
			// invalidate meta
			queryClient.invalidateQueries({ queryKey: reviewCycleDetailsQuery(variables.reviewCycleId).queryKey });
			// invalidate chunks - use a partial key to match all instances
			queryClient.invalidateQueries({
				queryKey: ["review-cycle-reviews-details", variables.reviewCycleId],
				exact: false,
			});
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useBulkDeactivateEasyAddFormsForReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) =>
			bulkDeactivateEasyAddFormsForReviewCycle(reviewCycleId),
		...options,
	});
};

export const useBulkApproveEasyAddFormsForReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) =>
			bulkApproveEasyAddFormsForReviewCycle(reviewCycleId),
		...options,
	});
};

export const useDeleteReviewCycle = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) => deleteReviewCycle(reviewCycleId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useDownloadGoalDiscussionCsv = (options?: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async ({ reviewCycleId }: { reviewCycleId: string }) => downloadGoalDiscussionCsv(reviewCycleId),
		...options,
		onSuccess: (response, variables, context, mutation) => {
			options?.onSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useCreateReviewCycle = (
	options?: PYUseMutationOptions<CreateReviewCycleResponseJson, Error, CreateReviewCycleRequestInput>
) => {
	return useMutation({
		mutationFn: createReviewCycle,
		meta: {
			errorMessage: "There was an error creating the review cycle.",
		},
		...options,
	});
};

export const useCreateReviewCyclePreviewSubjects = (
	options?: PYUseMutationOptions<
		PostReviewCyclePreviewSubjectsResponseJson,
		Error,
		PostReviewCyclePreviewSubjectsRequestInput
	>
) => {
	return useMutation({
		mutationFn: postReviewCyclePreviewSubjects,
		meta: {
			errorMessage: "There was an error generating the qualifying subjects preview.",
		},
		...options,
	});
};

export const useReviewCyclePreviewSubjectsResults = (
	{
		previewId,
		params,
	}: {
		previewId: string | null;
		params: GetReviewCyclePreviewSubjectsResultsRequestInput;
	},
	options?: PYUseQueryOptions<GetReviewCyclePreviewSubjectsResultsResponseJson>
) => {
	return useQuery<GetReviewCyclePreviewSubjectsResultsResponseJson>({
		queryKey: ["review-cycle-preview-subjects-results", previewId, params],
		queryFn: () => getReviewCyclePreviewSubjectsResults(previewId!, params),
		meta: {
			errorMessage: "There was an error fetching the qualifying subjects.",
		},
		enabled: !!previewId,
		placeholderData: keepPreviousData,
		...options,
	});
};
